// studioKeys — what a plain key does to the focused card on the Studio canvas
// (pen "Studio – detail design" §7). Pure.
//
//   Arrows        move the card 8 units; with Shift, 40
//   Enter         open it
//   Space         select or deselect it
//   Delete        remove it from the canvas (Backspace too: the Mac's
//                 "delete" key); never deletes the material
//   Esc           clear the selection
//
// A key with a modifier other than Shift is never ours (web chords belong to
// the browser), and none is claimed while a drag is in progress (the canvas
// owns the keys then: Esc, or switching what a drop makes).

export type { CardKeyPress };
export { cardKeyIntent };

import {
    ARROW_STEP,
    ARROW_STEP_LARGE,
    type Direction,
} from "./studioLayout.js";

type CardKey =
    | {
          readonly type: "nudge";
          readonly direction: Direction;
          readonly step: number;
      }
    | { readonly type: "open" }
    | { readonly type: "toggle-selected" }
    | { readonly type: "remove-from-canvas" }
    | { readonly type: "clear-selection" };

type CardKeyPress = {
    readonly key: string;
    readonly shift: boolean;
    // Cmd, Ctrl or Alt held.
    readonly modified: boolean;
    readonly dragging: boolean;
    readonly anySelected: boolean;
};

const ARROWS: Readonly<Record<string, Direction>> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
};

const cardKeyIntent = (press: CardKeyPress): CardKey | null => {
    if (press.modified || press.dragging) return null;
    const direction = ARROWS[press.key];
    if (direction !== undefined)
        return {
            type: "nudge",
            direction,
            step: press.shift ? ARROW_STEP_LARGE : ARROW_STEP,
        };
    switch (press.key) {
        case "Enter":
            return { type: "open" };
        case " ":
            return { type: "toggle-selected" };
        case "Delete":
        case "Backspace":
            return { type: "remove-from-canvas" };
        case "Escape":
            return press.anySelected ? { type: "clear-selection" } : null;
        default:
            return null;
    }
};
